/**
 * ItemRegistry — one façade over the per-kind registries.
 *
 * `weaponRegistry`, `abilityRegistry` and `consumableRegistry` each own their
 * own content and keep their own kind-specific API. This façade owns none of
 * it; it routes an item ref to whichever registry holds that kind, so code that
 * only needs "the item this ref names" (inventory rebuild, pickup derivation,
 * the pickup handlers) can stop caring which registry that is.
 *
 * Each environment registers the kinds it has: the server in
 * `src/server/runtime/registries.js`, the client in `client.js`'s config load.
 * A kind absent from an environment simply resolves to null there rather than
 * throwing — the client, for instance, has no server-side ability subclasses.
 */

import { parseRef, KINDS } from './ItemRef.js';
import { getSharedLogger } from './utils/loggerProxy.js';

const logger = getSharedLogger('GENERAL');

class ItemRegistry {
  constructor() {
    /** @type {Map<string, { get: Function, all: Function, defaultGraphic: string|null }>} */
    this.adapters = new Map();
  }

  /**
   * Point a kind at the registry that owns it.
   *
   * @param {string} kind one of ItemRef.KINDS
   * @param {{ get: (id: string) => object|null, all: () => object[], defaultGraphic?: string }} adapter
   *        `get`/`all` are usually thin wrappers over the real registry
   *        (`(id) => weaponRegistry.get(id)`), which keeps the façade from
   *        depending on any registry's method names.
   * @returns {boolean} whether the kind was registered
   */
  registerKind(kind, adapter) {
    if (!KINDS.includes(kind)) {
      logger.warn(`[itemRegistry] Refusing unknown kind '${kind}'; add it to ItemRef.KINDS first`);
      return false;
    }
    if (!adapter || typeof adapter.get !== 'function' || typeof adapter.all !== 'function') {
      logger.warn(`[itemRegistry] Refusing kind '${kind}': adapter needs both get(id) and all()`);
      return false;
    }
    if (this.adapters.has(kind)) {
      // Expected on the client (hot reload, a second config load); worth a line
      // because on the server it means two loaders are fighting over a kind.
      logger.warn(`[itemRegistry] Kind '${kind}' re-registered; replacing the previous adapter`);
    }

    this.adapters.set(kind, {
      get: adapter.get,
      all: adapter.all,
      defaultGraphic: adapter.defaultGraphic ?? null
    });
    return true;
  }

  /**
   * The item a ref names, or null.
   * @param {string|object} refOrString any form `parseRef` accepts
   * @param {object} [options] forwarded to parseRef (e.g. legacyPickupLookup)
   * @returns {object|null}
   */
  get(refOrString, options = {}) {
    const ref = parseRef(refOrString, options);
    if (!ref) return null;
    const adapter = this.adapters.get(ref.kind);
    if (!adapter) return null;
    return adapter.get(ref.id) || null;
  }

  /**
   * @param {string|object} refOrString
   * @param {object} [options]
   * @returns {boolean}
   */
  has(refOrString, options = {}) {
    return this.get(refOrString, options) !== null;
  }

  /**
   * Every item of one kind — the input to pickup derivation.
   * @param {string} kind
   * @returns {object[]} empty when the kind is not registered here
   */
  allOf(kind) {
    const adapter = this.adapters.get(kind);
    return adapter ? adapter.all() || [] : [];
  }

  /**
   * Every registered item across all kinds, paired with its kind.
   * @returns {Array<{ kind: string, item: object }>}
   */
  allEntries() {
    const entries = [];
    for (const kind of this.kinds()) {
      for (const item of this.allOf(kind)) entries.push({ kind, item });
    }
    return entries;
  }

  /** @returns {string[]} registered kinds, in registration order */
  kinds() {
    return [...this.adapters.keys()];
  }

  /**
   * The fallback pickup graphic for a kind, for items with no `icon` of their
   * own. Overridden by `pickups.json`'s `settings.graphics[kind]` at load.
   * @param {string} kind
   * @returns {string|null}
   */
  defaultGraphicFor(kind) {
    return this.adapters.get(kind)?.defaultGraphic ?? null;
  }

  /** Test seam: drop every kind. */
  clear() {
    this.adapters.clear();
  }
}

const itemRegistry = new ItemRegistry();

export { ItemRegistry, itemRegistry };
